import React, { useCallback, useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import styles from './styles';
import { ImageRotation } from 'shared/dtos/out/document';

const IMAGE_LOAD_TIMEOUT_MS = 15000;

type Props = {
  onError?: () => void;
  onClick: (e: React.SyntheticEvent) => void;
  setLoaded: () => void;
  description: string;
  src: string;
  imageRotation: ImageRotation;
} & {
  className: string;
};

const Image: React.FC<Props> = (props) => {
  const classes = styles();
  const imageRef = useRef<HTMLImageElement | null>(null);

  const {
    onClick,
    className,
    description,
    setLoaded,
    onError,
    src,
    imageRotation,
  } = props;

  const resolveImageState = useCallback(() => {
    const image = imageRef.current;

    if (!image || !image.complete) {
      return;
    }

    if (image.naturalWidth > 0) {
      setLoaded();
      return;
    }

    onError?.();
  }, [onError, setLoaded]);

  useEffect(() => {
    resolveImageState();

    const timeoutId = setTimeout(() => {
      const image = imageRef.current;

      if (!image) {
        return;
      }

      if (image.complete && image.naturalWidth > 0) {
        setLoaded();
        return;
      }

      onError?.();
    }, IMAGE_LOAD_TIMEOUT_MS);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [src, onError, setLoaded, resolveImageState]);

  const handleLoad = useCallback(() => {
    resolveImageState();
  }, [resolveImageState]);

  const handleError = useCallback(() => {
    onError?.();
  }, [onError]);

  return (
    <div
      onClick={onClick}
      className={cn(classes.imageContainer, className)}
    >
      <img
        ref={imageRef}
        // s3 + chrome - big issue around Origin and CORS - just use 'use-credentials' for every request to s3 PT-2785
        crossOrigin={'use-credentials'}
        className={classes.image}
        style={{
          transform: `rotate(${imageRotation ?? 0}deg)`,
        }}
        alt={description}
        loading='eager'
        decoding='async'
        onLoad={handleLoad}
        onError={handleError}
        src={src}
      />
    </div>
  );
};
export default Image;
